import { useEffect, useState } from 'react';
import { Info } from 'lucide-react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { campaignsApi } from '@/api/client';
import type { CampaignAdProduct } from '@/types/spCampaigns';
import type { HourlyBudgetSchedulePayload } from '@/types/hourlyBudget';
import { DeleteHourlyBudgetModal } from '@/components/DeleteHourlyBudgetModal';
import { HourlyBudgetWeekGrid } from '@/components/hourlyBudget/HourlyBudgetWeekGrid';
import { cn } from '@/utils/cn';
import {
  baseBudgetMin,
  baseWindowsFromApi,
  baseWindowsToPayload,
  parseBaseBudgetForPayload,
  hourlyBudgetFromApiResponse,
  validateBaseWindows,
  validateTimeWindows,
  windowsFromApi,
  windowsToPayload,
  type HourlyBudgetBaseWindowDraft,
  type HourlyBudgetTimeWindowDraft,
} from '@/utils/hourlyBudgetSchedule';
import {
  canToggleHourOn,
  hourlyBudgetFormSnapshot,
  type HourSlotMap,
} from '@/utils/hourlyBudgetHourSlots';
import {
  campaignHourlyBudgetQueryKey,
  campaignHourlyBudgetRunLogsQueryKey,
} from '@/utils/hourlyBudgetList';
import {
  ALL_WEEKDAYS,
  WEEKDAYS,
  allDayWeeklySlots,
  collapseWeeklySlotsToWindows,
  daySettingsFromWeeklyAmounts,
  emptyWeeklyAmounts,
  emptyWeeklySlots,
  expandWindowsToWeeklySlots,
  weeklyAmountsFromApi,
  type DayAmounts,
  type WeekdayIndex,
} from '@/utils/hourlyBudgetWeek';
import { cloneWeeklySlots } from '@/utils/daypartingAutomationPrefill';

export type HourlyBudgetEditorProps = {
  profileId: string;
  campaignRef?: string;
  product?: CampaignAdProduct;
  onSaved?: () => void;
  onDeleted?: () => void;
  hideTitleBar?: boolean;
  /** Heatmap hours to select after hydrate; not written into the saved baseline. */
  initialWeeklySlots?: Record<WeekdayIndex, HourSlotMap>;
  skipFetch?: boolean;
  hydrateFrom?: Record<string, unknown> | null;
  persistOverride?: (payload: HourlyBudgetSchedulePayload) => Promise<void>;
  deleteOverride?: () => Promise<void>;
  saveDisabled?: boolean;
  hideDelete?: boolean;
};

function slotsAreEmpty(slotsByDay: Record<WeekdayIndex, HourSlotMap>): boolean {
  return ALL_WEEKDAYS.every((day) => Object.keys(slotsByDay[day] ?? {}).length === 0);
}

export function HourlyBudgetEditor({
  profileId,
  campaignRef,
  product,
  onSaved,
  onDeleted,
  hideTitleBar = false,
  initialWeeklySlots,
  skipFetch = false,
  hydrateFrom,
  persistOverride,
  deleteOverride,
  saveDisabled = false,
  hideDelete = false,
}: HourlyBudgetEditorProps) {
  const qc = useQueryClient();
  const [hourlyBudgetInput, setHourlyBudgetInput] = useState('');
  const [baseBudgetInput, setBaseBudgetInput] = useState('');
  const [hourlyBudgetActive, setHourlyBudgetActive] = useState(true);
  const [weeklySlots, setWeeklySlots] = useState<Record<WeekdayIndex, HourSlotMap>>(emptyWeeklySlots);
  const [weeklyAmounts, setWeeklyAmounts] = useState<Record<WeekdayIndex, DayAmounts>>(emptyWeeklyAmounts);
  const [hourGridMessage, setHourGridMessage] = useState<string | null>(null);
  const [windowValidationErrors, setWindowValidationErrors] = useState<string[]>([]);
  const [hourlyFormHydrated, setHourlyFormHydrated] = useState(false);
  const [hourlyBudgetBaseline, setHourlyBudgetBaseline] = useState<string | null>(null);
  const [savingHourlyBudget, setSavingHourlyBudget] = useState(false);
  const [hourlyBudgetSaveError, setHourlyBudgetSaveError] = useState<string | null>(null);
  const [deleteHourlyBudgetOpen, setDeleteHourlyBudgetOpen] = useState(false);
  const [deleteHourlyBudgetError, setDeleteHourlyBudgetError] = useState<string | null>(null);
  const [deletingHourlyBudget, setDeletingHourlyBudget] = useState(false);
  const compact = hideTitleBar;

  const hourlyBudgetQueryKey = campaignHourlyBudgetQueryKey(profileId, campaignRef || 'group');
  const hourlyRunLogsQueryKey = campaignHourlyBudgetRunLogsQueryKey(profileId, campaignRef || 'group');

  const {
    data: fetchedHourlyBudget,
    isLoading: isHourlyBudgetLoading,
    error: hourlyBudgetError,
  } = useQuery({
    queryKey: hourlyBudgetQueryKey,
    queryFn: async () => {
      const res = await campaignsApi.getCampaignHourlyBudget(profileId, campaignRef || '', {
        product,
      });
      if (!res.success) throw new Error('Failed to load hourly budget');
      return res.data as Record<string, unknown> | null;
    },
    enabled: Boolean(profileId && campaignRef) && !skipFetch,
  });

  const hourlyBudgetData = skipFetch
    ? ((hydrateFrom ?? null) as Record<string, unknown> | null)
    : fetchedHourlyBudget;

  useEffect(() => {
    setHourlyFormHydrated(false);
    setHourlyBudgetBaseline(null);
  }, [profileId, campaignRef, skipFetch]);

  const formSnapshot = (
    hourly: string,
    base: string,
    active: boolean,
    wAmounts: Record<WeekdayIndex, DayAmounts>,
    wSlots: Record<WeekdayIndex, HourSlotMap>
  ) =>
    hourlyBudgetFormSnapshot(hourly, base, active, {}, {
      scheduleMode: 'weekly',
      weeklyAmounts: wAmounts,
      weeklySlots: wSlots,
    });

  const resetHourlyBudgetForm = () => {
    const amounts = emptyWeeklyAmounts();
    const slotsByDay = emptyWeeklySlots();
    setHourlyBudgetInput('');
    setBaseBudgetInput('');
    setHourlyBudgetActive(true);
    setWeeklySlots(slotsByDay);
    setWeeklyAmounts(amounts);
    setHourGridMessage(null);
    setWindowValidationErrors([]);
    setHourlyBudgetSaveError(null);
    setHourlyBudgetBaseline(formSnapshot('', '', true, amounts, slotsByDay));
  };

  const applyHourlyBudgetToForm = (row: Record<string, unknown>) => {
    const hourly = row.hourly_budget != null && row.hourly_budget !== '' ? String(row.hourly_budget) : '';
    const baseBudget =
      row.base_budget != null && row.base_budget !== '' ? String(row.base_budget) : '';
    const active = row.active !== false;
    const bases = baseWindowsFromApi(row);
    const times = windowsFromApi(row.time_windows);
    let slotsByDay = expandWindowsToWeeklySlots(bases, times);
    if (slotsAreEmpty(slotsByDay) && bases.length === 0) {
      slotsByDay = allDayWeeklySlots();
    }
    const amounts = weeklyAmountsFromApi(row.day_settings, hourly, baseBudget);
    setHourlyBudgetInput(hourly);
    setBaseBudgetInput(baseBudget);
    setHourlyBudgetActive(active);
    setWeeklySlots(slotsByDay);
    setWeeklyAmounts(amounts);
    setHourGridMessage(null);
    setWindowValidationErrors([]);
    setHourlyBudgetBaseline(formSnapshot(hourly, baseBudget, active, amounts, slotsByDay));
  };

  useEffect(() => {
    if (hourlyFormHydrated || isHourlyBudgetLoading) return;
    if (hourlyBudgetData) {
      applyHourlyBudgetToForm(hourlyBudgetData);
    } else {
      resetHourlyBudgetForm();
    }
    if (initialWeeklySlots) {
      setWeeklySlots(cloneWeeklySlots(initialWeeklySlots));
    }
    setHourlyFormHydrated(true);
  }, [hourlyBudgetData, hourlyFormHydrated, isHourlyBudgetLoading, initialWeeklySlots]);

  const scheduleTimezoneLabel =
    typeof hourlyBudgetData?.schedule_timezone_label === 'string'
      ? hourlyBudgetData.schedule_timezone_label
      : 'IST (India Standard Time, UTC+5:30)';

  const hasHourlyBudgetChanges =
    hourlyBudgetBaseline != null &&
    formSnapshot(hourlyBudgetInput, baseBudgetInput, hourlyBudgetActive, weeklyAmounts, weeklySlots) !==
      hourlyBudgetBaseline;

  const selectedHourCount = ALL_WEEKDAYS.reduce<number>(
    (sum, day) => sum + Object.keys(weeklySlots[day] ?? {}).length,
    0
  );

  const invalidateHourlyBudgetQueries = async () => {
    await qc.invalidateQueries({ queryKey: hourlyBudgetQueryKey });
    await qc.invalidateQueries({ queryKey: hourlyRunLogsQueryKey });
    await qc.invalidateQueries({ queryKey: ['profile-hourly-budgets', profileId] });
  };

  const persistHourlyBudget = async (
    bases: HourlyBudgetBaseWindowDraft[],
    windows: HourlyBudgetTimeWindowDraft[],
    options?: {
      baseBudgetOverride?: number | null;
      daySettings?: { day_of_week: number; hourly_budget: number | null; base_budget: number | null }[];
      hourlyOverride?: number | null;
    }
  ) => {
    const hourlyTrimmed = hourlyBudgetInput.trim();
    const parsedHourly = hourlyTrimmed ? parseFloat(hourlyTrimmed) : NaN;
    const hasHourly = !Number.isNaN(parsedHourly) && parsedHourly > 0;

    const baseCheck = validateBaseWindows(bases);
    if (!baseCheck.ok) {
      setWindowValidationErrors(baseCheck.errors);
      throw new Error('Fix base window errors before saving.');
    }

    const windowCheck = validateTimeWindows(windows, bases);
    if (!windowCheck.ok) {
      setWindowValidationErrors(windowCheck.errors);
      throw new Error('Fix time window errors before saving.');
    }

    let baseBudgetValue: number | null;
    if (options?.baseBudgetOverride !== undefined) {
      baseBudgetValue = options.baseBudgetOverride;
    } else {
      const baseBudgetParsed = parseBaseBudgetForPayload(baseBudgetInput);
      if (baseBudgetParsed.error) {
        throw new Error(baseBudgetParsed.error);
      }
      baseBudgetValue = baseBudgetParsed.value;
    }

    const hourlyValue: number | null =
      options?.hourlyOverride !== undefined ? options.hourlyOverride : hasHourly ? parsedHourly : null;

    const daySettings = options?.daySettings;
    if (daySettings === undefined) {
      throw new Error('Weekly schedule is missing day amounts.');
    }

    const payload: HourlyBudgetSchedulePayload = {
      hourly_budget: hourlyValue,
      base_budget: baseBudgetValue,
      active: hourlyBudgetActive,
      schedule_mode: 'weekly',
      day_settings: daySettings,
      base_windows: baseWindowsToPayload(bases),
      time_windows: windowsToPayload(windows),
    };

    if (persistOverride) {
      await persistOverride(payload);
      setHourlyBudgetBaseline(
        formSnapshot(
          hourlyBudgetInput,
          baseBudgetInput,
          hourlyBudgetActive,
          weeklyAmounts,
          weeklySlots
        )
      );
      setHourlyFormHydrated(true);
      await invalidateHourlyBudgetQueries();
      onSaved?.();
      return;
    }

    if (!campaignRef) {
      throw new Error('Select at least one campaign');
    }

    const res = await campaignsApi.updateCampaignHourlyBudget(
      profileId,
      campaignRef,
      payload,
      { product }
    );
    if (!res.success) {
      throw new Error('Failed to save hourly budget');
    }
    const saved = hourlyBudgetFromApiResponse(res.data);
    if (saved) {
      qc.setQueryData(hourlyBudgetQueryKey, saved);
      applyHourlyBudgetToForm(saved);
      setHourlyFormHydrated(true);
    }
    await invalidateHourlyBudgetQueries();
    onSaved?.();
  };

  const parseHourlyValue = (raw: string): number | null => {
    const trimmed = raw.trim();
    if (!trimmed) return null;
    const parsed = parseFloat(trimmed);
    if (Number.isNaN(parsed) || parsed <= 0) return null;
    return parsed;
  };

  const collectPersistPayload = () => {
    const settings = daySettingsFromWeeklyAmounts(
      weeklyAmounts,
      parseHourlyValue,
      parseBaseBudgetForPayload
    );
    if (!settings.ok) throw new Error(settings.error);
    if (settings.settings.length === 0) {
      throw new Error(
        `Enter at least a base budget${baseBudgetMin() != null ? ` (min ${baseBudgetMin()})` : ''} or an hourly budget greater than 0 for one weekday.`
      );
    }
    const { bases, windows } = collapseWeeklySlotsToWindows(weeklySlots);
    return {
      bases,
      windows,
      daySettings: settings.settings,
      hourly: settings.settings[0]?.hourly_budget ?? null,
      base: settings.settings[0]?.base_budget ?? null,
    };
  };

  const submitHourlyBudget = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!hasHourlyBudgetChanges) return;
    setHourlyBudgetSaveError(null);
    setWindowValidationErrors([]);

    setSavingHourlyBudget(true);
    try {
      const payload = collectPersistPayload();
      await persistHourlyBudget(payload.bases, payload.windows, {
        daySettings: [...payload.daySettings],
        hourlyOverride: payload.hourly,
        baseBudgetOverride: payload.base,
      });
    } catch (err) {
      const ax = err as { response?: { data?: { detail?: unknown } } };
      const detail = ax.response?.data?.detail;
      if (detail && typeof detail === 'object' && detail !== null && 'errors' in detail) {
        const errs = (detail as { errors: { message?: string }[] }).errors;
        setWindowValidationErrors(errs.map((x) => x.message || 'Invalid window').filter(Boolean));
      }
      setHourlyBudgetSaveError((err as Error).message || 'Failed to save hourly budget');
    } finally {
      setSavingHourlyBudget(false);
    }
  };

  const handleToggleWeekHour = (day: WeekdayIndex, hour: number) => {
    const slots = weeklySlots[day] ?? {};
    setWeeklySlots((prev) => {
      const current = prev[day] ?? {};
      if (current[hour] != null) {
        const next = { ...current };
        delete next[hour];
        return { ...prev, [day]: next };
      }
      if (!canToggleHourOn(current, hour)) return prev;
      return { ...prev, [day]: { ...current, [hour]: '1' } };
    });
    setHourGridMessage(
      slots[hour] == null && !canToggleHourOn(slots, hour)
        ? `At most 12 separate hour ranges allowed on ${WEEKDAYS[day].long}`
        : null
    );
  };

  const handleWeekMultiplierChange = (day: WeekdayIndex, hour: number, multiplier: string) => {
    setWeeklySlots((prev) => {
      const current = prev[day] ?? {};
      if (current[hour] == null) return prev;
      return { ...prev, [day]: { ...current, [hour]: multiplier } };
    });
  };

  const handleWeekAmountChange = (day: WeekdayIndex, field: 'hourly' | 'base', value: string) => {
    setWeeklyAmounts((prev) => ({
      ...prev,
      [day]: { ...(prev[day] ?? { hourly: '', base: '' }), [field]: value },
    }));
  };

  const clearHourSelection = () => {
    setWeeklySlots(emptyWeeklySlots());
    setHourGridMessage(null);
  };

  const clearWeekDayHours = (day: WeekdayIndex) => {
    setWeeklySlots((prev) => ({ ...prev, [day]: {} }));
    setHourGridMessage(null);
  };

  const applyCopyToDays = (sourceDay: WeekdayIndex, targetDays: WeekdayIndex[]) => {
    const sourceSlots = weeklySlots[sourceDay] ?? {};
    const sourceAmounts = weeklyAmounts[sourceDay] ?? { hourly: '', base: '' };
    setWeeklySlots((prev) => {
      const next = { ...prev };
      for (const day of targetDays) next[day] = { ...sourceSlots };
      return next;
    });
    setWeeklyAmounts((prev) => {
      const next = { ...prev };
      for (const day of targetDays) next[day] = { ...sourceAmounts };
      return next;
    });
  };

  const requestDeleteHourlyBudget = () => {
    setDeleteHourlyBudgetError(null);
    setDeleteHourlyBudgetOpen(true);
  };

  const handleConfirmDeleteHourlyBudget = async () => {
    setDeletingHourlyBudget(true);
    setDeleteHourlyBudgetError(null);
    try {
      if (deleteOverride) {
        await deleteOverride();
        resetHourlyBudgetForm();
        setHourlyFormHydrated(false);
        setDeleteHourlyBudgetOpen(false);
        await invalidateHourlyBudgetQueries();
        onDeleted?.();
        return;
      }
      if (!campaignRef) throw new Error('Select at least one campaign');
      const res = await campaignsApi.deleteCampaignHourlyBudget(profileId, campaignRef, {
        product,
      });
      if (!res.success) throw new Error('Failed to remove hourly budget');
      qc.setQueryData(hourlyBudgetQueryKey, null);
      qc.setQueryData(hourlyRunLogsQueryKey, []);
      resetHourlyBudgetForm();
      setHourlyFormHydrated(false);
      setDeleteHourlyBudgetOpen(false);
      await invalidateHourlyBudgetQueries();
      onDeleted?.();
    } catch (err) {
      setDeleteHourlyBudgetError((err as Error).message || 'Failed to remove hourly budget');
    } finally {
      setDeletingHourlyBudget(false);
    }
  };

  const statusClass = compact ? 'text-sm text-foreground-muted mb-2' : 'text-sm text-foreground-muted mb-4';

  return (
    <>
      <div
        className={cn(
          'bg-surface',
          compact ? 'px-5 pb-4 pt-4' : 'rounded-xl border border-border px-6 pb-6 pt-6 shadow'
        )}
      >
        {!compact && (
          <div className="mb-2 flex flex-wrap items-start justify-between gap-3">
            <h2 className="text-lg font-semibold text-foreground">Hourly budget</h2>
            {hourlyBudgetData && !hideDelete && (
              <button
                type="button"
                onClick={requestDeleteHourlyBudget}
                disabled={
                  savingHourlyBudget ||
                  deletingHourlyBudget ||
                  isHourlyBudgetLoading
                }
                className="rounded-lg border border-error/20 bg-surface px-3 py-1.5 text-sm font-medium text-error-ink hover:bg-error-subtle disabled:opacity-50"
              >
                Remove hourly budget
              </button>
            )}
          </div>
        )}
        <p
          className={cn(
            'rounded-xl border border-warning/20 bg-warning-subtle px-3 py-2 text-sm text-warning-ink',
            compact ? 'mb-2 flex items-start gap-2' : 'mb-4'
          )}
        >
          {compact ? <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden /> : null}
          <span>
            All schedule times are in <strong>{scheduleTimezoneLabel}</strong>. Window times use{' '}
            <strong>whole hours only</strong> (:00 minutes). End hour must be after start hour.
          </span>
        </p>
        {hourlyBudgetData && hourlyBudgetData.outside_operating_window === true && (
          <p className={statusClass}>
            Outside operating window — no increment scheduled this hour (
            {String(hourlyBudgetData.evaluated_at_ist ?? 'IST')}).
          </p>
        )}
        {hourlyBudgetData &&
          (hourlyBudgetData.hourly_budget == null || hourlyBudgetData.hourly_budget === '') &&
          hourlyBudgetData.base_budget != null &&
          hourlyBudgetData.base_budget !== '' && (
            <p className={statusClass}>
              Midnight reset only — Amazon daily budget is set to{' '}
              <strong>{String(hourlyBudgetData.base_budget)}</strong> at 12:00 AM IST. No hourly
              increments until an hourly budget is configured.
            </p>
          )}
        {hourlyBudgetData &&
          hourlyBudgetData.hourly_budget != null &&
          hourlyBudgetData.hourly_budget !== '' &&
          hourlyBudgetData.outside_operating_window !== true &&
          hourlyBudgetData.current_effective_increment != null &&
          hourlyBudgetData.current_multiplier != null && (
            <p className={statusClass}>
              Right now ({String(hourlyBudgetData.evaluated_at_ist ?? 'IST')}): increment{' '}
              <strong>{String(hourlyBudgetData.current_effective_increment)}</strong> (
              {String(hourlyBudgetData.current_multiplier)}× hourly budget{' '}
              {String(hourlyBudgetData.hourly_budget ?? (hourlyBudgetInput || '—'))})
            </p>
          )}
        <form
          onSubmit={submitHourlyBudget}
          className={cn(compact ? 'mb-3' : 'mb-5 rounded-xl border border-border bg-page p-4')}
        >
          {!compact && (
            <h3 className="mb-3 text-sm font-semibold text-foreground">Configure hourly budget</h3>
          )}
          <div className={cn(compact ? 'mt-3' : 'mt-6 border-t border-border pt-4')}>
            <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
              <h4 className="text-sm font-semibold text-foreground">Hourly budget schedule</h4>
              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  disabled={savingHourlyBudget || selectedHourCount === 0}
                  onClick={clearHourSelection}
                  className="rounded-lg border border-border bg-surface px-3 py-1.5 text-sm font-medium hover:bg-page disabled:opacity-50"
                >
                  Clear selection
                </button>
              </div>
            </div>
            <div className="mb-3">
              <label className="flex items-center gap-2 text-sm text-foreground-muted">
                <input
                  type="checkbox"
                  checked={hourlyBudgetActive}
                  onChange={(e) => setHourlyBudgetActive(e.target.checked)}
                  disabled={savingHourlyBudget || deletingHourlyBudget}
                />
                Active
              </label>
            </div>
            <HourlyBudgetWeekGrid
              slotsByDay={weeklySlots}
              amountsByDay={weeklyAmounts}
              timezoneLabel={scheduleTimezoneLabel}
              disabled={savingHourlyBudget || deletingHourlyBudget}
              validationMessage={hourGridMessage}
              onToggleHour={handleToggleWeekHour}
              onMultiplierChange={handleWeekMultiplierChange}
              onAmountChange={handleWeekAmountChange}
              onCopyDay={applyCopyToDays}
              onClearDay={clearWeekDayHours}
            />
          </div>
          {windowValidationErrors.length > 0 && (
            <ul className="mt-3 list-disc list-inside text-sm text-error">
              {windowValidationErrors.map((msg) => (
                <li key={msg}>{msg}</li>
              ))}
            </ul>
          )}
          {hourlyBudgetSaveError && (
            <p className="mt-2 text-sm text-error">{hourlyBudgetSaveError}</p>
          )}
          <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
            <button
              type="submit"
              disabled={
                savingHourlyBudget ||
                deletingHourlyBudget ||
                isHourlyBudgetLoading ||
                saveDisabled ||
                !hasHourlyBudgetChanges
              }
              className="rounded-lg bg-brand px-4 py-2 text-sm font-medium text-white disabled:opacity-50"
            >
              {savingHourlyBudget ? 'Saving...' : 'Save hourly budget'}
            </button>
          </div>
        </form>
        {isHourlyBudgetLoading && !skipFetch && (
          <p className="mt-2 text-sm text-foreground-subtle">Loading hourly budget...</p>
        )}
        {hourlyBudgetError && !skipFetch && (
          <p className="mt-2 text-sm text-error">
            {(hourlyBudgetError as Error).message || 'Failed to load hourly budget'}
          </p>
        )}
      </div>
      <DeleteHourlyBudgetModal
        isOpen={deleteHourlyBudgetOpen}
        hourlyBudget={String(hourlyBudgetData?.hourly_budget ?? (hourlyBudgetInput || ''))}
        deleting={deletingHourlyBudget}
        error={deleteHourlyBudgetError}
        onConfirm={handleConfirmDeleteHourlyBudget}
        onCancel={() => {
          if (!deletingHourlyBudget) {
            setDeleteHourlyBudgetOpen(false);
            setDeleteHourlyBudgetError(null);
          }
        }}
      />
    </>
  );
}
